import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import { competitionRoot } from '../services/competitionApi';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Target, 
  Plus,
  Search,
  X
} from 'lucide-react';
import dayjs from 'dayjs';
import { getTeamDisplayName } from '../utils/teamDisplayName';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/Button';
import { saveAdminUserBet } from '../components/admin/AdminBetScoreModal';

const AdminBets = () => {
  const { language, isRTL } = useLanguage();
  const { competitionSlug } = useCompetition();
  const gamesBase = () => `${competitionRoot(competitionSlug)}/games`;
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchUserTerm, setSearchUserTerm] = useState('');
  const [searchGameTerm, setSearchGameTerm] = useState('');
  
  const [betForm, setBetForm] = useState({
    userId: '',
    gameId: '',
    homeScore: '',
    awayScore: ''
  });
  const [existingBet, setExistingBet] = useState(null);
  const [includeFinishedGames, setIncludeFinishedGames] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionSlug]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, gamesRes] = await Promise.all([
        axios.get('/users'),
        axios.get(gamesBase())
      ]);
      setUsers(usersRes.data);
      setGames(gamesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(language === 'en' ? 'Failed to load data' : 'נכשל בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadExistingBet = async () => {
      if (!betForm.userId || !betForm.gameId) {
        setExistingBet(null);
        return;
      }
      try {
        const betsResponse = await axios.get(`/users/${betForm.userId}/bets`);
        const found = (betsResponse.data || []).find((bet) => {
          const gid = bet.gameId?._id || bet.gameId || bet.game?._id || bet.game;
          return String(gid) === String(betForm.gameId);
        });
        if (found) {
          setExistingBet(found);
          const pred = found.predictedResult;
          if (pred && typeof pred === 'object' && pred.homeScore != null) {
            setBetForm((prev) => ({
              ...prev,
              homeScore: String(pred.homeScore),
              awayScore: String(pred.awayScore)
            }));
          }
        } else {
          setExistingBet(null);
          setBetForm((prev) => ({ ...prev, homeScore: '', awayScore: '' }));
        }
      } catch {
        setExistingBet(null);
      }
    };
    loadExistingBet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betForm.userId, betForm.gameId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!betForm.userId || !betForm.gameId) {
      toast.error(language === 'en' ? 'Please select both user and game' : 'אנא בחר משתמש ומשחק');
      return;
    }

    if (betForm.homeScore === '' || betForm.awayScore === '') {
      toast.error(language === 'en' ? 'Please enter both scores' : 'אנא הזן את שתי התוצאות');
      return;
    }

    const homeScore = parseInt(betForm.homeScore, 10);
    const awayScore = parseInt(betForm.awayScore, 10);

    if (Number.isNaN(homeScore) || Number.isNaN(awayScore) || homeScore < 0 || awayScore < 0) {
      toast.error(language === 'en' ? 'Scores must be valid non-negative numbers' : 'התוצאות חייבות להיות מספרים תקינים לא שליליים');
      return;
    }

    const selectedGame = games.find((g) => String(g._id) === String(betForm.gameId));
    const finished = Boolean(selectedGame?.isFinished);

    if (finished) {
      const ok = window.confirm(
        language === 'en'
          ? "This game is finished and points have already been calculated. Saving will recalculate this user's points. Continue?"
          : 'המשחק הסתיים והנקודות כבר חושבו. השמירה תחשב מחדש את הנקודות למשתמש זה. להמשיך?'
      );
      if (!ok) return;
    }

    try {
      const bet = await saveAdminUserBet({
        userId: betForm.userId,
        gameId: betForm.gameId,
        predictedResult: { homeScore, awayScore }
      });

      if (finished) {
        toast.success(
          language === 'en'
            ? `Bet saved. Points updated (${bet?.points ?? 0}).`
            : `ההימור נשמר. הנקודות עודכנו (${bet?.points ?? 0}).`
        );
      } else if (existingBet) {
        toast.success(language === 'en' ? 'Bet updated successfully!' : 'ההימור עודכן בהצלחה!');
      } else {
        toast.success(language === 'en' ? 'Bet created successfully!' : 'ההימור נוצר בהצלחה!');
      }
      
      setShowModal(false);
      setExistingBet(null);
      setBetForm({ userId: '', gameId: '', homeScore: '', awayScore: '' });
    } catch (error) {
      console.error('Error creating/updating bet:', error);
      const message = error.response?.data?.message || (language === 'en' ? 'Failed to save bet' : 'נכשל בשמירת ההימור');
      toast.error(message);
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
    (user.fullName && user.fullName.toLowerCase().includes(searchUserTerm.toLowerCase()))
  );

  const filteredGames = games.filter(game => {
    const homeTeam = typeof game.homeGroupId === 'object' ? game.homeGroupId : {};
    const awayTeam = typeof game.awayGroupId === 'object' ? game.awayGroupId : {};
    
    const matchesSearch = !searchGameTerm || 
      getTeamDisplayName(homeTeam, 'en').toLowerCase().includes(searchGameTerm.toLowerCase()) ||
      getTeamDisplayName(homeTeam, 'he').toLowerCase().includes(searchGameTerm.toLowerCase()) ||
      getTeamDisplayName(awayTeam, 'en').toLowerCase().includes(searchGameTerm.toLowerCase()) ||
      getTeamDisplayName(awayTeam, 'he').toLowerCase().includes(searchGameTerm.toLowerCase()) ||
      (game.round && game.round.toLowerCase().includes(searchGameTerm.toLowerCase()));
    
    if (!matchesSearch) return false;
    if (!includeFinishedGames && game.isFinished) return false;
    return true;
  });

  if (loading) {
    return (
      <AdminLayout
        title={language === 'en' ? 'Bet management' : 'ניהול הימורים'}
        subtitle={language === 'en' ? 'Create or edit user picks' : 'יצירה ועריכה של ניבויים'}
        loading
      />
    );
  }

  return (
    <AdminLayout
      title={language === 'en' ? 'Bet management' : 'ניהול הימורים'}
      subtitle={
        language === 'en'
          ? 'Add or update picks for users, including finished games.'
          : 'הוסף או עדכן ניבויים למשתמשים, כולל משחקים שהסתיימו.'
      }
      actions={
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            setExistingBet(null);
            setBetForm({ userId: '', gameId: '', homeScore: '', awayScore: '' });
            setShowModal(true);
          }}
        >
          <Plus className="h-4 w-4" />
          {language === 'en' ? 'Add bet' : 'הוסף הימור'}
        </Button>
      }
    >

        {/* Info Banner */}
        <div style={{
          background: 'linear-gradient(90deg, #fef3c7 0%, #fde68a 100%)',
          border: '1px solid #f59e0b',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: '#f59e0b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: 16,
            flexShrink: 0
          }}>
            ⚠️
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: 14 }}>
              {language === 'en' ? 'Admin Privilege' : 'הרשאות מנהל'}
            </div>
            <div style={{ color: '#b45309', fontSize: 13, marginTop: 2 }}>
              {language === 'en' 
                ? 'You can add or edit bets for any game, including finished games. Points are recalculated automatically.'
                : 'ניתן להוסיף או לערוך הימורים לכל משחק, כולל משחקים שהסתיימו. הנקודות מחושבות מחדש אוטומטית.'}
            </div>
          </div>
        </div>

        {/* Bet Creation Modal */}
        {showModal && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}>
            <div style={{ 
              background: 'white', 
              borderRadius: 16, 
              padding: 32, 
              width: '90%', 
              maxWidth: 600,
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#18181b' }}>
                  {existingBet
                    ? language === 'en' ? 'Edit Bet for User' : 'ערוך הימור למשתמש'
                    : language === 'en' ? 'Add Bet for User' : 'הוסף הימור למשתמש'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setExistingBet(null);
                    setBetForm({ userId: '', gameId: '', homeScore: '', awayScore: '' });
                  }}
                  style={{ 
                    background: '#fee2e2', 
                    color: '#18181b', 
                    border: 'none', 
                    borderRadius: 8, 
                    padding: 8, 
                    cursor: 'pointer' 
                  }}
                >
                  <X style={{ width: 20, height: 20 }} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit}>
                {/* User Selection */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#18181b' }}>
                    {language === 'en' ? 'Select User' : 'בחר משתמש'} *
                  </label>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <Search style={{ 
                      position: 'absolute', 
                      left: isRTL ? 'auto' : 12, 
                      right: isRTL ? 12 : 'auto', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: '#6b7280', 
                      width: 20, 
                      height: 20 
                    }} />
                    <input
                      type="text"
                      placeholder={language === 'en' ? 'Search users...' : 'חפש משתמשים...'}
                      value={searchUserTerm}
                      onChange={(e) => setSearchUserTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        paddingLeft: isRTL ? 16 : 44,
                        paddingRight: isRTL ? 44 : 16,
                        border: '2px solid #fee2e2',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }}
                    />
                  </div>
                  <select
                    value={betForm.userId}
                    onChange={(e) => setBetForm({ ...betForm, userId: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #fee2e2',
                      borderRadius: 8,
                      fontSize: 16,
                      outline: 'none',
                      background: 'white',
                      color: '#18181b',
                      maxHeight: 200,
                      overflowY: 'auto'
                    }}
                  >
                    <option value="">
                      {language === 'en' ? 'Select a user' : 'בחר משתמש'}
                    </option>
                    {filteredUsers.map(user => (
                      <option key={user._id} value={user._id}>
                        {user.fullName || user.username} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Game Selection */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#18181b' }}>
                    {language === 'en' ? 'Select Game' : 'בחר משחק'} *
                  </label>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <Search style={{ 
                      position: 'absolute', 
                      left: isRTL ? 'auto' : 12, 
                      right: isRTL ? 12 : 'auto', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: '#6b7280', 
                      width: 20, 
                      height: 20 
                    }} />
                    <input
                      type="text"
                      placeholder={language === 'en' ? 'Search games...' : 'חפש משחקים...'}
                      value={searchGameTerm}
                      onChange={(e) => setSearchGameTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        paddingLeft: isRTL ? 16 : 44,
                        paddingRight: isRTL ? 44 : 16,
                        border: '2px solid #fee2e2',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }}
                    />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={includeFinishedGames}
                      onChange={(e) => setIncludeFinishedGames(e.target.checked)}
                    />
                    {language === 'en' ? 'Include finished games' : 'כלול משחקים שהסתיימו'}
                  </label>
                  <select
                    value={betForm.gameId}
                    onChange={(e) => setBetForm({ ...betForm, gameId: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #fee2e2',
                      borderRadius: 8,
                      fontSize: 16,
                      outline: 'none',
                      background: 'white',
                      color: '#18181b',
                      maxHeight: 200,
                      overflowY: 'auto'
                    }}
                  >
                    <option value="">
                      {language === 'en' ? 'Select a game' : 'בחר משחק'}
                    </option>
                    {filteredGames.map(game => {
                      const homeTeam = typeof game.homeGroupId === 'object' ? game.homeGroupId : {};
                      const awayTeam = typeof game.awayGroupId === 'object' ? game.awayGroupId : {};
                      const gameTime = dayjs(`${game.date} ${game.hour}`);
                      const isPast = gameTime.isBefore(dayjs());
                      const statusLabel = game.isFinished
                        ? (language === 'en' ? '(Finished)' : '(הסתיים)')
                        : isPast
                          ? (language === 'en' ? '(Past)' : '(עבר)')
                          : '';
                      
                      return (
                        <option key={game._id} value={game._id}>
                          {getTeamDisplayName(homeTeam, language) || 'Home'} vs {getTeamDisplayName(awayTeam, language) || 'Away'} - {gameTime.format('DD/MM/YYYY HH:mm')} {statusLabel} {game.round ? `- ${game.round}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {existingBet ? (
                  <div style={{ marginBottom: 16, padding: 12, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, fontSize: 13, color: '#1e40af' }}>
                    {language === 'en'
                      ? 'This user already has a bet for this game. Saving will update it.'
                      : 'למשתמש זה כבר יש הימור למשחק זה. השמירה תעדכן אותו.'}
                    {existingBet.isCalculated ? ` (${language === 'en' ? 'Current points' : 'נקודות נוכחיות'}: ${existingBet.points})` : ''}
                  </div>
                ) : null}

                {games.find((g) => String(g._id) === String(betForm.gameId))?.isFinished ? (
                  <div style={{ marginBottom: 16, padding: 12, background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                    {language === 'en'
                      ? 'This game is finished. Saving will recalculate this user\'s points from the new prediction.'
                      : 'המשחק הסתיים. השמירה תחשב מחדש את הנקודות למשתמש זה לפי הניבוי החדש.'}
                  </div>
                ) : null}

                {/* Bet Scores */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 16, marginBottom: 24 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#18181b', textAlign: 'center' }}>
                      {language === 'en' ? 'Home Score' : 'תוצאת בית'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={betForm.homeScore}
                      onChange={(e) => setBetForm({ ...betForm, homeScore: e.target.value })}
                      required
                      placeholder="0"
                      style={{ 
                        width: '100%', 
                        padding: 12, 
                        borderRadius: 8, 
                        border: '2px solid #fee2e2', 
                        background: 'white', 
                        color: '#18181b', 
                        fontSize: 18, 
                        textAlign: 'center',
                        fontWeight: 600
                      }}
                    />
                  </div>
                  
                  <div style={{ 
                    fontSize: 32, 
                    fontWeight: 700, 
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 60
                  }}>
                    -
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#18181b', textAlign: 'center' }}>
                      {language === 'en' ? 'Away Score' : 'תוצאת חוץ'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={betForm.awayScore}
                      onChange={(e) => setBetForm({ ...betForm, awayScore: e.target.value })}
                      required
                      placeholder="0"
                      style={{ 
                        width: '100%', 
                        padding: 12, 
                        borderRadius: 8, 
                        border: '2px solid #fee2e2', 
                        background: 'white', 
                        color: '#18181b', 
                        fontSize: 18, 
                        textAlign: 'center',
                        fontWeight: 600
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setExistingBet(null);
                      setBetForm({ userId: '', gameId: '', homeScore: '', awayScore: '' });
                    }}
                    style={{ 
                      background: '#f3f4f6', 
                      color: '#374151', 
                      fontWeight: 600, 
                      padding: '12px 24px', 
                      borderRadius: 8, 
                      border: 'none', 
                      cursor: 'pointer' 
                    }}
                  >
                    {language === 'en' ? 'Cancel' : 'ביטול'}
                  </button>
                  <button
                    type="submit"
                    style={{ 
                      background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)', 
                      color: 'white', 
                      fontWeight: 600, 
                      padding: '12px 24px', 
                      borderRadius: 8, 
                      border: 'none', 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <Target style={{ width: 18, height: 18 }} />
                    {existingBet
                      ? (language === 'en' ? 'Save Bet' : 'שמור הימור')
                      : (language === 'en' ? 'Create Bet' : 'צור הימור')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div style={{ 
          background: '#fef2f2', 
          borderRadius: 12, 
          padding: 20, 
          marginBottom: 24, 
          border: '1px solid #fee2e2' 
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#18181b', marginBottom: 12 }}>
            💡 {language === 'en' ? 'How to use' : 'איך להשתמש'}
          </h3>
          <ul style={{ color: '#dc2626', fontSize: 14, lineHeight: 1.6, paddingLeft: isRTL ? 0 : 20, paddingRight: isRTL ? 20 : 0 }}>
            <li style={{ marginBottom: 8 }}>
              {language === 'en' ? 'Click "Add Bet" to create or edit a bet for a user' : 'לחץ על "הוסף הימור" כדי ליצור או לערוך הימור למשתמש'}
            </li>
            <li style={{ marginBottom: 8 }}>
              {language === 'en' ? 'Select a user and a game (including finished games)' : 'בחר משתמש ומשחק (כולל משחקים שהסתיימו)'}
            </li>
            <li style={{ marginBottom: 8 }}>
              {language === 'en' ? 'Enter the predicted scores for both teams' : 'הזן את התוצאות הצפויות עבור שתי הקבוצות'}
            </li>
            <li>
              {language === 'en' ? 'If the game is already finished, the user’s points will be recalculated automatically' : 'אם המשחק כבר הסתיים, הנקודות של המשתמש יחושבו מחדש אוטומטית'}
            </li>
          </ul>
        </div>
    </AdminLayout>
  );
};

export default AdminBets;

